import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Youtube, ExternalLink, RefreshCw, Save } from "lucide-react";
import api, { apiErr } from "@/lib/api";

// Extract Channel ID from full YouTube URL or accept a raw UC… id.
function parseChannelId(input = "") {
  const s = input.trim();
  const m = s.match(/channel\/(UC[A-Za-z0-9_-]{20,24})/);
  if (m) return m[1];
  if (/^UC[A-Za-z0-9_-]{20,24}$/.test(s)) return s;
  return "";
}

export default function VideosAdmin() {
  const [setting, setSetting] = useState({ channel_id: "", channel_url: "", cache_updated_at: null, cached_count: 0 });
  const [videos, setVideos] = useState([]);
  const [channelInput, setChannelInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, v] = await Promise.all([
        api.get("/settings/youtube-channel"),
        api.get("/public/videos?limit=15"),
      ]);
      setSetting(s.data);
      setChannelInput(s.data.channel_id || "");
      setVideos(v.data || []);
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const r = await api.post("/tools/youtube-channel/refresh");
      setOk(`Refresh हो गया — ${r.data.count} वीडियो मिले`);
      setTimeout(() => setOk(""), 3000);
      await load();
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || "Refresh विफल");
    } finally { setBusy(false); }
  };

  const saveChannel = async () => {
    const cid = parseChannelId(channelInput);
    if (!cid) { setErr("मान्य Channel ID या URL डालें (UC… से शुरू)"); return; }
    setBusy(true); setErr(""); setOk("");
    try {
      const r = await api.put("/settings/youtube-channel", { channel_id: cid });
      setOk(`Channel सेट हुआ — ${r.data.count} वीडियो sync हुए`);
      setTimeout(() => setOk(""), 3000);
      await load();
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || "सेव नहीं हुआ");
    } finally { setBusy(false); }
  };

  const cacheAge = () => {
    if (!setting.cache_updated_at) return "अभी fetch नहीं हुआ";
    const d = new Date(setting.cache_updated_at);
    const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
    return mins === 0 ? "अभी-अभी" : `${mins} मिनट पहले`;
  };

  return (
    <div className="max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-serif-dev">वीडियो · YouTube Auto-Sync</h1>
        <p className="text-sm text-gray-500 mt-1">
          YouTube पर वीडियो अपलोड करें — अपने आप website के <b>देनवा टीवी</b> section में title और thumbnail के साथ आ जाएगा।
          15 latest videos हर 15 मिनट में refresh होते हैं।
        </p>
      </div>

      {err && <div className="text-sm text-[#D92525] bg-red-50 rounded px-4 py-2.5" data-testid="videos-error">{err}</div>}
      {ok && <div className="text-sm text-green-700 bg-green-50 rounded px-4 py-2.5 flex items-center gap-2" data-testid="videos-ok"><CheckCircle2 size={15} /> {ok}</div>}

      {/* Channel settings */}
      <section className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-4" data-testid="youtube-channel-settings">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Youtube size={20} className="text-[#D92525]" /> YouTube Channel
        </h2>

        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Channel ID या URL</span>
          <div className="mt-1 flex gap-2 flex-wrap">
            <input
              data-testid="youtube-channel-input"
              value={channelInput}
              onChange={(e) => setChannelInput(e.target.value)}
              placeholder="UCcqGx9N1ozUx3BNIphjlBig  या  https://www.youtube.com/channel/UC..."
              className="flex-1 min-w-[280px] border border-gray-200 rounded-lg py-2.5 px-3 focus:border-[#D92525] outline-none font-mono text-sm"
            />
            <button
              onClick={saveChannel}
              disabled={busy}
              data-testid="youtube-channel-save"
              className="inline-flex items-center gap-2 bg-[#D92525] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0A0F1E] disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} सहेजें
            </button>
          </div>
        </label>

        <div className="flex items-center justify-between flex-wrap gap-3 text-sm text-gray-600 pt-2 border-t border-gray-100">
          <div>
            <div>Current: <span className="font-mono text-[13px]">{setting.channel_id || "—"}</span></div>
            {setting.channel_url && (
              <a href={setting.channel_url} target="_blank" rel="noreferrer" className="text-[#D92525] hover:underline inline-flex items-center gap-1 text-xs mt-1">
                YouTube पर खोलें <ExternalLink size={12} />
              </a>
            )}
            <div className="text-xs text-gray-400 mt-1">Last sync: {cacheAge()} · Cached: {setting.cached_count} videos</div>
          </div>
          <button
            onClick={refresh}
            disabled={busy}
            data-testid="youtube-channel-refresh"
            className="inline-flex items-center gap-2 border border-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} अभी Refresh करें
          </button>
        </div>
      </section>

      {/* Live sync preview */}
      <section className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900">Live Sync ({videos.length} videos)</h3>
          <span className="text-xs text-gray-400">Auto-fetched from YouTube · read-only</span>
        </div>
        {loading ? (
          <div className="py-10 text-center text-gray-400"><Loader2 size={24} className="animate-spin mx-auto" /></div>
        ) : videos.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center">
            <Youtube size={32} className="mx-auto text-gray-300 mb-2" />
            कोई वीडियो नहीं मिला। Channel ID जांचें या Refresh दबाएँ।
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[640px] overflow-y-auto pr-1">
            {videos.map((v) => (
              <li key={v.id} data-testid={`video-list-${v.youtube_id}`}
                className="border border-gray-100 rounded-lg p-2.5 flex gap-3 items-start hover:bg-gray-50">
                <a href={v.youtube_url} target="_blank" rel="noreferrer" className="shrink-0">
                  <img src={v.thumb} alt={v.title} className="w-32 aspect-video object-cover rounded" />
                </a>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#D92525] font-semibold uppercase tracking-wider">{v.category}</div>
                  <div className="text-sm text-gray-800 font-medium leading-snug line-clamp-2 mt-0.5">{v.title}</div>
                  <a href={v.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-gray-500 hover:text-[#D92525] inline-flex items-center gap-1 mt-1">
                    YouTube पर देखें <ExternalLink size={11} />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
